import { getLocaleFromLocaleCode } from '@pkg/config/languages';
import type { LOCALE_CODE } from '@pkg/config/languages/localeCodes';
import { useLocaleOptions } from '@pkg/site-chrome/composables/useLocaleOptions';
import { useSanityData } from '@pkg/sanity-utils/composables/useSanityData';
import { pathPartsToSlugParts, urlToPathParts } from '~/app/helpers/sanity/urlToSlugParts';
import type { PageContent } from '~/app/features/cms/types/PageContent';
import { pageBySlug } from '~/apis/sanity/queries/pageBySlug';

export function useLocale(onChange?: (locale: LOCALE_CODE) => void) {
  const { locale: localeCode } = useI18n();
  const currentLocale = getLocaleFromLocaleCode(localeCode.value as LOCALE_CODE);
  const { options } = useLocaleOptions();

  const selectLanguage = async(locale: LOCALE_CODE) => {
    const router = useRouter();
    const route = useRoute();
    const localePath = useLocalePath();
    // route.path is already locale-prefixed; urlToPathParts() handles stripping it.
    const pathParts = urlToPathParts(route.path);
    const slugParts = pathPartsToSlugParts(pathParts);
    onChange?.(locale);

    const sanityData = await useSanityData<{ page?: PageContent | null }>(
      pageBySlug(slugParts),
      { locale: unref(locale), ...slugParts, slug: slugParts?.slug || '' },
    );

    if (sanityData) {
      // If sanity data is available and contains non-empty page data, redirect to the new page
      const { page } = sanityData.data.value ?? { page: null };
      const slug = page?.slug ?? '/';
      router.push(localePath(slug, locale));
    }
  };

  return {
    currentLocale,
    options,
    selectLanguage,
  };
}
